/**
 * N1 · Details step 2 of 4 — MOBILE NUMBER only.
 * Uses the shared PhoneField; validation comes from @/lib/details-steps.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { readDetails, readSelectedVariant, saveDetails } from "@/lib/consumer-session";
import PhoneField from "@/components/consumer/PhoneField";
import DetailsStepPage from "@/components/consumer/DetailsStepPage";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S6_SECTION, S6_DEFAULTS } from "@/content/screens/s6";
import {
  firstIncompleteStep,
  nextDetailsStep,
  phoneDigitsOf,
  phoneMissing,
  prevDetailsStep,
} from "@/lib/details-steps";
import { useConsumerFlow } from "../../ConsumerFlow";
import { useFieldCompletion } from "@/hooks/useFieldCompletion";
import { useKnownDetailsPrefill } from "@/lib/details-prefill";

export default function PhoneStep() {
  const { program } = useConsumerFlow();
  const navigate = useNavigate();
  const { t } = useSiteContent(S6_SECTION, S6_DEFAULTS);
  const existing = readDetails(program.slug);
  const [phone, setPhone] = useState(existing?.phone ?? "");

  useEffect(() => {
    if (!readSelectedVariant(program.slug)) {
      navigate("../reveal", { replace: true });
      return;
    }
    if (firstIncompleteStep(readDetails(program.slug), 0) === "name") {
      navigate("../name", { replace: true });
    }
  }, [program.slug, navigate]);

  /* PREFILL the stored mobile number; editable, and saved to this order only. */
  useKnownDetailsPrefill(program.slug, (known) => {
    setPhone((v) => v || known.phone || "");
  });

  const missing = phoneMissing(phone);
  useFieldCompletion("field_phone_complete", !missing);

  function onContinue() {
    saveDetails(program.slug, {
      ...(readDetails(program.slug) ?? {}),
      phone: phoneDigitsOf(phone) || null,
    } as any);
    navigate(nextDetailsStep("phone"));
  }

  return (
    <DetailsStepPage
      testId="s6-phone-step"
      backTo={prevDetailsStep("phone")}
      backLabel={t("s6.header.backLabel")}
      step="2 of 4"
      title={t("s6.contact.phoneLabel")}
      missing={missing}
      continueLabel={t("s6.footer.continue")}
      onContinue={onContinue}
    >
      <PhoneField
        value={phone}
        onChange={setPhone}
        label={t("s6.contact.phoneLabel")}
        hint={t("s6.contact.phoneHint")}
        placeholder={t("s6.contact.phonePlaceholder")}
        testId="s6-phone"
      />
    </DetailsStepPage>
  );
}
